import React from "react";
import { Input } from "antd";

/**
 * Shared helpers for Timelog "Actual Quantity" / "Actual Rate" inputs and the
 * amount calculations derived from them. Used by the Timelog Create, Edit and
 * View flows so the rules stay consistent everywhere.
 *
 * Rules (locked with product):
 *  - Inputs accept digits only with at most 2 decimal places. Pasting a value
 *    with more decimals is silently truncated (e.g. 12.999 -> 12.99).
 *  - Resource line total = actualResourceCount x actualQuantity x actualRate.
 *  - Tool line total     = actualQuantity x actualRate (no count).
 */

// Valid decimal value: digits, optional single dot, max 2 decimal places.
export const DECIMAL_REGEX = /^\d+(\.\d{1,2})?$/;

// Max digits allowed before the decimal point (matches backend decimal(18,2)
// usage / the app's 10-digit numeric convention).
export const MAX_DECIMAL_INTEGER_DIGITS = 10;

// Strip anything that is not a digit or a dot, collapse to a single decimal
// point, truncate to a maximum of 2 decimal places, and cap the integer part
// to `maxIntegerDigits` digits.
export const sanitizeDecimalInput = (
  raw: unknown,
  maxIntegerDigits: number = MAX_DECIMAL_INTEGER_DIGITS,
): string => {
  if (raw === null || raw === undefined) return "";
  let value = String(raw).replace(/[^0-9.]/g, "");

  // Keep only the first dot.
  const firstDot = value.indexOf(".");
  if (firstDot !== -1) {
    value =
      value.slice(0, firstDot + 1) +
      value.slice(firstDot + 1).replace(/\./g, "");
  }

  // Cap the integer part to maxIntegerDigits.
  const dot = value.indexOf(".");
  const intPart = dot === -1 ? value : value.slice(0, dot);
  const rest = dot === -1 ? "" : value.slice(dot);
  if (maxIntegerDigits > 0 && intPart.length > maxIntegerDigits) {
    value = intPart.slice(0, maxIntegerDigits) + rest;
  }

  // Truncate to 2 decimal places.
  const dotIndex = value.indexOf(".");
  if (dotIndex !== -1) {
    value = value.slice(0, dotIndex + 1) + value.slice(dotIndex + 1, dotIndex + 3);
  }

  return value;
};

// True when the value is a non-empty, well-formed decimal with <= 2 places.
export const isValidDecimal = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return false;
  return DECIMAL_REGEX.test(String(value));
};

// Safe numeric coercion: null/empty/invalid -> 0.
export const toNumber = (value: unknown): number => {
  if (value === "" || value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

// Round to 2 decimals avoiding floating point drift.
export const round2 = (n: number): number =>
  Math.round((Number(n) + Number.EPSILON) * 100) / 100;

// Currency formatting used across the app (INR, 2 decimals).
export const formatCurrency = (value: number): string =>
  `₹${(Number.isFinite(value) ? value : 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

// Line totals.
export const resourceLineTotal = (
  count: unknown,
  quantity: unknown,
  rate: unknown,
): number => round2(toNumber(count) * toNumber(quantity) * toNumber(rate));

export const toolLineTotal = (quantity: unknown, rate: unknown): number =>
  round2(toNumber(quantity) * toNumber(rate));

// Control keys allowed while typing in a decimal field.
const DECIMAL_CONTROL_KEYS = [
  "Backspace",
  "Delete",
  "Tab",
  "Escape",
  "Enter",
  "ArrowLeft",
  "ArrowRight",
  "ArrowUp",
  "ArrowDown",
  "Home",
  "End",
];

/**
 * Controlled decimal input designed to be dropped inside an antd `Form.Item`
 * (the Form injects `value`/`onChange`). Blocks invalid keystrokes and
 * sanitizes pasted content down to a max-2-decimal numeric string.
 */
export const DecimalInput: React.FC<{
  value?: string | number;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
  addonAfter?: React.ReactNode;
  addonBefore?: React.ReactNode;
  maxIntegerDigits?: number;
}> = ({
  value,
  onChange,
  placeholder,
  disabled,
  style,
  addonAfter,
  addonBefore,
  maxIntegerDigits = MAX_DECIMAL_INTEGER_DIGITS,
}) => {
  const stringValue =
    value === null || value === undefined ? "" : String(value);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    if (DECIMAL_CONTROL_KEYS.includes(e.key)) return;
    if (e.key === ".") {
      // Only a single decimal point is allowed.
      if (stringValue.includes(".")) e.preventDefault();
      return;
    }
    // Block anything that is not a single digit (letters, emojis, symbols).
    if (!/^[0-9]$/.test(e.key)) {
      e.preventDefault();
      return;
    }
    const dotIndex = stringValue.indexOf(".");
    const hasSelection =
      e.currentTarget.selectionStart !== e.currentTarget.selectionEnd;
    // Block typing a 3rd decimal digit.
    if (dotIndex !== -1) {
      const decimals = stringValue.slice(dotIndex + 1);
      const caretInDecimals =
        (e.currentTarget.selectionStart ?? 0) > dotIndex;
      if (decimals.length >= 2 && caretInDecimals && !hasSelection) {
        e.preventDefault();
        return;
      }
    }
    // Block exceeding the max integer-digit count.
    const intPart = dotIndex === -1 ? stringValue : stringValue.slice(0, dotIndex);
    const caretInInteger =
      (e.currentTarget.selectionStart ?? 0) <=
      (dotIndex === -1 ? stringValue.length : dotIndex);
    if (
      maxIntegerDigits > 0 &&
      intPart.length >= maxIntegerDigits &&
      caretInInteger &&
      !hasSelection
    ) {
      e.preventDefault();
    }
  };

  return (
    <Input
      value={stringValue}
      placeholder={placeholder}
      disabled={disabled}
      inputMode="decimal"
      style={{ width: "100%", ...style }}
      addonAfter={addonAfter}
      addonBefore={addonBefore}
      onKeyDown={handleKeyDown}
      // onChange catches paste/auto-fill and sanitizes (truncates) the result.
      onChange={(e) =>
        onChange?.(sanitizeDecimalInput(e.target.value, maxIntegerDigits))
      }
    />
  );
};

// antd Form.Item rules for a mandatory decimal field.
export const decimalFieldRules = (label: string) => [
  { required: true, message: `${label} is required` },
  {
    validator: (_: unknown, v: unknown) => {
      if (v === "" || v === null || v === undefined) return Promise.resolve();
      return isValidDecimal(v)
        ? Promise.resolve()
        : Promise.reject(
            new Error(`${label} must be a number with up to 2 decimals`),
          );
    },
  },
];
